import { createTheme, alpha } from '@mui/material/styles';

const slate = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
};

const primary = '#5a67d8';
const primaryDark = '#4c51bf';
const primaryLight = '#7f8ce8';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: primary,
      dark: primaryDark,
      light: primaryLight,
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: slate[800],
      secondary: slate[500],
    },
    divider: slate[200],
    grey: {
      50: slate[50],
      100: slate[100],
      200: slate[200],
      300: slate[300],
      400: slate[400],
      500: slate[500],
      600: slate[600],
      700: slate[700],
      800: slate[800],
      900: slate[900],
    },
    error: { main: '#ef4444' },
    warning: { main: '#f59e0b' },
    info: { main: '#3b82f6' },
    success: { main: '#22c55e' },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 16,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontWeight: 600, fontSize: '2.25rem', letterSpacing: '-0.02em', lineHeight: 1.25 },
    h2: { fontWeight: 600, fontSize: '1.625rem', letterSpacing: '-0.015em', lineHeight: 1.3 },
    h3: { fontWeight: 600, fontSize: '1.375rem', letterSpacing: '-0.01em', lineHeight: 1.35 },
    h4: { fontWeight: 600, fontSize: '1.1875rem', letterSpacing: '-0.01em', lineHeight: 1.4 },
    h5: { fontWeight: 600, fontSize: '1.0625rem', lineHeight: 1.45 },
    h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
    subtitle1: { fontWeight: 500, fontSize: '1.0625rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.55 },
    body2: { fontSize: '0.9375rem', lineHeight: 1.55 },
    button: { textTransform: 'none', fontWeight: 500, fontSize: '0.9375rem', letterSpacing: '0.01em' },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
    '0 2px 8px -2px rgba(15, 23, 42, 0.08)',
    '0 4px 16px -4px rgba(15, 23, 42, 0.1)',
    '0 8px 24px -6px rgba(15, 23, 42, 0.12)',
    '0 16px 40px -8px rgba(15, 23, 42, 0.14)',
    ...Array(19).fill('0 16px 40px -8px rgba(15, 23, 42, 0.14)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '1rem',
          lineHeight: 1.55,
          scrollbarColor: `${slate[300]} transparent`,
          '&::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: slate[300], borderRadius: 8 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, textTransform: 'none', fontWeight: 500, fontSize: '0.9375rem' },
        containedPrimary: {
          background: `linear-gradient(135deg, ${primary} 0%, #6b76e0 50%, ${primaryLight} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 50%, #6b76e0 100%)`,
            boxShadow: '0 4px 20px rgba(90, 103, 216, 0.45)',
          },
        },
        outlined: {
          borderColor: slate[200],
          color: slate[600],
          '&:hover': { borderColor: slate[300], bgcolor: slate[50] },
        },
        sizeSmall: { padding: '6px 14px', fontSize: '0.875rem' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${slate[200]}`,
          boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.06)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: slate[200], fontSize: '1rem', fontWeight: 400, lineHeight: 1.55 },
        head: {
          fontWeight: 600,
          fontSize: '0.8125rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: slate[500],
          bgcolor: slate[50],
          borderBottom: `1px solid ${slate[200]}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': { borderBottom: 0 },
          '&:hover': { bgcolor: alpha(primary, 0.04) },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: '0 20px 40px -12px rgba(15, 23, 42, 0.15)',
          border: `1px solid ${slate[200]}`,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          bgcolor: '#ffffff',
          '& fieldset': { borderColor: slate[200] },
          '&:hover fieldset': { borderColor: slate[300] },
          '&.Mui-focused fieldset': { borderColor: primary },
        },
        inputSizeSmall: { fontSize: '0.9375rem', padding: '9px 12px', lineHeight: 1.5 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: '0.8125rem', borderRadius: 8 },
        sizeSmall: { height: 24 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
        standardSuccess: { bgcolor: alpha('#22c55e', 0.1), color: '#15803d' },
        standardError: { bgcolor: alpha('#ef4444', 0.1), color: '#b91c1c' },
        standardWarning: { bgcolor: alpha('#f59e0b', 0.1), color: '#b45309' },
        standardInfo: { bgcolor: alpha('#3b82f6', 0.1), color: '#1d4ed8' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.12)',
          background: 'linear-gradient(180deg, #161d2b 0%, #1a2332 55%, #1e293b 100%)',
        },
      },
    },
  },
});

theme.palette.sidebar = {
  bg: 'transparent',
  bgSolid: '#1a2332',
  bgElevated: 'rgba(255, 255, 255, 0.05)',
  border: 'rgba(255, 255, 255, 0.08)',
  text: 'rgba(255, 255, 255, 0.58)',
  textMuted: 'rgba(255, 255, 255, 0.38)',
  textActive: 'rgba(255, 255, 255, 0.95)',
  hover: 'rgba(255, 255, 255, 0.05)',
  active: 'rgba(90, 103, 216, 0.14)',
  accent: primary,
};

export default theme;
